import React from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-types-papi';
import StandardThumb from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Standard';
import { OverlayContextProvider } from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Standard/Overlay/OverlayContext';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import { isDeviationTextEditable } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import Button from '@wix/da-shared-react/pkg/Button';
import LoadableDuperbrowseContext from '../../../../../contexts/LoadableDuperbrowseContext';
import EmptyState from '../EmptyState';

import s from './FeaturedList.scss';

export interface Props {
  streamId: string;
  items: PapiDeviation[];
  fetchMore: () => void;
  editItem: (deviation: PapiDeviation) => void;
  unfeatureItem: (deviation: PapiDeviation) => void;
  hasMore: boolean;
  isFetching: boolean;
  isEmpty: boolean;
  isOwner: boolean;
}

const FeaturedList: React.FC<Props> = ({
  items,
  streamId,
  isFetching,
  hasMore,
  fetchMore,
  editItem,
  unfeatureItem,
  isEmpty,
  isOwner,
}) => {
  const { t } = useTranslation();
  if (isEmpty) {
    return (
      <EmptyState
        view="small"
        postType="featuredJournal"
        className={s['empty-state']}
      />
    );
  }

  return (
    <div className={s['root']}>
      <LoadableDuperbrowseContext
        streamId={streamId}
        parent={{ title: t('navigation.route.posts') }}
      >
        <div className={s['items']}>
          <OverlayContextProvider
            value={{
              showExtraActions: true,
            }}
          >
            {items.map((item, index) => (
              <StandardThumb
                key={`${index}-${item.typeId}-${item.deviationId}-${item['isPinned']}`}
                width={300}
                height={280}
                deviation={item}
                className={s['item']}
                extraActions={
                  !isOwner
                    ? undefined
                    : [
                        {
                          label: t('widgets.feed.unfeature'),
                          onClick: () => unfeatureItem(item),
                        },
                        isDeviationTextEditable(item) && {
                          label: t('widgets.feed.edit'),
                          onClick: () => editItem(item),
                        },
                      ]
                }
              />
            ))}
          </OverlayContextProvider>

          {isFetching && <LoadingIndicator />}
          {hasMore && (
            <div className={s['button-wrapper']}>
              <Button view="b3" onClick={fetchMore} disabled={isFetching}>
                {t('common.loadMore')}
              </Button>
            </div>
          )}
        </div>
      </LoadableDuperbrowseContext>
    </div>
  );
};
FeaturedList.displayName = 'FeaturedList';

export default FeaturedList;
